/**
 * Sección pública para explicar e impulsar la instalación PWA.
 *
 * Flujo contemplado:
 * - Android/Chrome y navegadores compatibles con `beforeinstallprompt`.
 * - iOS/Safari, donde la instalación requiere usar "Compartir > Añadir a inicio".
 * - Estado ya instalado para evitar mostrar CTAs redundantes.
 *
 * Este bloque vive en la landing y no dentro de los paneles.
 */
import React, { useEffect, useMemo, useState } from "react";
import "./PwaInstallSection.scss";

const isIosSafari = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/crios|fxios/.test(userAgent);
  return isIos && isSafari;
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const getDeviceLabel = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  if (/android/.test(userAgent)) return "Android";
  if (/iphone|ipad|ipod/.test(userAgent)) return "iOS";
  return "tu dispositivo";
};

const PwaInstallSection = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [isIos, setIsIos] = useState(false);
  const [installFeedback, setInstallFeedback] = useState("");

  useEffect(() => {
    setIsIos(isIosSafari());

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setInstallFeedback("Mentfull ya quedó instalada en este dispositivo.");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const installMode = useMemo(() => {
    if (installed) return "installed";
    if (deferredPrompt) return "prompt";
    if (isIos) return "ios";
    return "manual";
  }, [deferredPrompt, installed, isIos]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === "accepted") {
      setInstallFeedback("Instalación iniciada. Sigue los pasos del sistema.");
    } else {
      setInstallFeedback(
        "Puedes instalar Mentfull después desde el menú del navegador."
      );
    }

    setDeferredPrompt(null);
  };

  return (
    <section id="instalar" className="pwa-install-section">
      <div className="pwa-install-shell">
        <div className="pwa-copy">
          <span className="eyebrow">Mentfull PWA</span>
          <h2>Lleva Mentfull contigo desde el navegador</h2>
          <p>
            Instala la app en {getDeviceLabel()} para entrar más rápido, usar
            pantalla completa y sentir la experiencia más cercana a una app
            nativa sin pasar todavía por App Store o Google Play.
          </p>

          <div className="install-benefits">
            <div className="benefit-card">
              <strong>Acceso inmediato</strong>
              <span>Ícono directo en la pantalla de inicio.</span>
            </div>
            <div className="benefit-card">
              <strong>Experiencia limpia</strong>
              <span>Se abre como app y aprovecha el modo standalone.</span>
            </div>
            <div className="benefit-card">
              <strong>Lista para crecer</strong>
              <span>Base preparada para la siguiente etapa mobile.</span>
            </div>
          </div>
        </div>

        <div className="pwa-install-card">
          <div className="phone-preview">
            <img src="/mentfull_ico.png" alt="Mentfull App Icon" />
            <div>
              <strong>Mentfull</strong>
              <span>Fitness, mente y seguimiento</span>
            </div>
          </div>

          {installMode === "installed" && (
            <div className="install-state success">
              <h3>Ya está instalada</h3>
              <p>
                Esta instalación ya corre como app web. Puedes abrirla desde tu
                pantalla de inicio.
              </p>
            </div>
          )}

          {installMode === "prompt" && (
            <div className="install-state ready">
              <h3>Instalación disponible</h3>
              <p>
                Tu navegador permite instalar Mentfull en un par de toques.
              </p>
              <button className="btn-accion install-button" onClick={handleInstall}>
                Instalar Mentfull
              </button>
            </div>
          )}

          {installMode === "ios" && (
            <div className="install-state info">
              <h3>Instálala en iPhone o iPad</h3>
              <ol>
                <li>Abre Mentfull en Safari.</li>
                <li>Toca el botón Compartir.</li>
                <li>Selecciona "Añadir a pantalla de inicio".</li>
              </ol>
            </div>
          )}

          {installMode === "manual" && (
            <div className="install-state info">
              <h3>Instalación manual</h3>
              <p>
                Si no aparece el botón automático, abre el menú del navegador y
                busca "Instalar app", "Añadir a pantalla de inicio" o una opción
                similar.
              </p>
            </div>
          )}

          <div className="install-tips">
            <h4>Recomendaciones</h4>
            <ul>
              <li>Usa Chrome o Edge en Android para ver el prompt nativo.</li>
              <li>Usa Safari en iOS para añadirla a la pantalla de inicio.</li>
              <li>Una vez instalada, inicia sesión como siempre.</li>
            </ul>
          </div>

          {installFeedback && <p className="install-feedback">{installFeedback}</p>}
        </div>
      </div>
    </section>
  );
};

export default PwaInstallSection;
